import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { UIProvider } from '../features/ui/UIContext';
import { AuthProvider } from '../features/auth/AuthContext';
import { AuthModal } from '../features/auth/AuthModal';
import { CartProvider } from '../features/cart/CartContext';
import { CartModal, type Quote } from '../features/cart/CartModal';
import { CheckoutModal } from '../features/cart/CheckoutModal';
import { ShipModal } from '../features/cart/ShipModal';
import { CounselModal, type CounselPreset } from '../features/growth/CounselModal';
import { DoubtModal } from '../features/growth/DoubtModal';
import { ContactFloats } from './ContactFloats';
import { Footer } from './Footer';
import { Header } from './Header';
import { Toast } from './Toast';

/**
 * App shell. Header and footer are light on every route; the dark theme is
 * scoped to Home only (spec 7.1) when that page lands in Phase 9.
 *
 * The cart/checkout/ship modals live here rather than in a page because the
 * cart button is in the header and must open from anywhere — mirroring the
 * prototype, where they were top-level modals in the document.
 */
export function Layout() {
  const { pathname } = useLocation();
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState<{ quote: Quote; coupon: string | null } | null>(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [counsel, setCounsel] = useState<CounselPreset | null>(null);
  const [doubtSlug, setDoubtSlug] = useState<string | null>(null);

  const toast = useCallback((message: string) => {
    // Re-arming with the same string must still re-show the bar; nudging the
    // state to null first makes the effect re-run rather than no-op.
    setToastMsg(null);
    setTimeout(() => setToastMsg(message), 0);
  }, []);

  const ui = useMemo(
    () => ({
      openCart: () => setCartOpen(true),
      openAuth: (m: 'login' | 'signup') => setAuthMode(m),
      toast,
      openCounsel: (preset?: CounselPreset) => setCounsel(preset ?? {}),
      openDoubt: (slug: string) => setDoubtSlug(slug),
    }),
    [toast],
  );

  return (
    <AuthProvider>
      <CartProvider>
        <UIProvider value={ui}>
        {!isAdminRoute ? <Header onOpenAuth={setAuthMode} onOpenCart={() => setCartOpen(true)} /> : null}
        <Outlet />
        {!isAdminRoute ? <Footer /> : null}
        {/* WhatsApp + call floats — the prototype's .wa-float is global (fixed on
            every page), so it lives in the shell, not per-page (spec 7.2). */}
        <ContactFloats />

        {authMode ? <AuthModal mode={authMode} onClose={() => setAuthMode(null)} /> : null}

        {cartOpen ? (
          <CartModal
            onClose={() => setCartOpen(false)}
            onCheckoutCourses={(quote, coupon) => {
              setCartOpen(false);
              setCheckout({ quote, coupon });
            }}
            onCheckoutBooks={() => {
              setCartOpen(false);
              setShipOpen(true);
            }}
          />
        ) : null}

        {checkout ? (
          <CheckoutModal quote={checkout.quote} coupon={checkout.coupon} onClose={() => setCheckout(null)} />
        ) : null}

        {shipOpen ? <ShipModal onClose={() => setShipOpen(false)} /> : null}

        {counsel ? (
          <CounselModal preset={counsel} onClose={() => setCounsel(null)} onBooked={toast} />
        ) : null}

        {doubtSlug ? <DoubtModal courseSlug={doubtSlug} onClose={() => setDoubtSlug(null)} /> : null}

        {/* .toast has been in base.css since Phase 3 with nothing to render it. */}
        <Toast message={toastMsg} onDone={() => setToastMsg(null)} />
        </UIProvider>
      </CartProvider>
    </AuthProvider>
  );
}
